// Photo library analysis ("Media"): the admin page starts runs and reads
// reports; a Kinmantle agent on the household's PC does the work against a
// home network share. Types and validators shared by the Worker, the admin
// page and (by contract) the Python agent in agent/.
//
// The full catalog (every path, metadata, previews) stays on the PC. Only
// run state and aggregate reports pass through the Worker.

// analyze_sample: step 2 on the fixed 500-file sample; analyze: the whole library;
// update: "Process new photos" (an incremental scan, then analysis of new or changed photos only).
export const MEDIA_RUN_KINDS = ["inventory", "benchmark", "analyze_sample", "analyze", "update"] as const;
export type MediaRunKind = (typeof MEDIA_RUN_KINDS)[number];

/** What actually happened, as reported by the agent (or the Worker for queued runs). */
export const MEDIA_RUN_STATUSES = ["queued", "running", "paused", "completed", "failed", "cancelled"] as const;
export type MediaRunStatus = (typeof MEDIA_RUN_STATUSES)[number];

/** What the admin asked for. The agent reads it on every heartbeat. */
export type MediaRunControl = "run" | "pause" | "cancel";

export const FINISHED_STATUSES: readonly MediaRunStatus[] = ["completed", "failed", "cancelled"];

/** An agent counts as online when it has checked in within this window. */
export const AGENT_ONLINE_MS = 90_000;
/** A run's lease lapses (agent gone) after this long without a heartbeat. */
export const RUN_LEASE_MS = 120_000;
export const PAIRING_CODE_MINUTES = 15;

/** Folders that QNAP and Windows keep for themselves; always skipped. */
export const SYSTEM_EXCLUSIONS = ["@Recycle", "@Recently-Snapshot", "@eaDir", ".@__thumb", "#recycle", "$RECYCLE.BIN", "System Volume Information"] as const;

export interface MediaAgentInfo {
  version: string;
  os: string;
  cpu: string;
  ramGb: number | null;
  gpu: string | null;
  /** Tool name → version, e.g. { exiftool: "13.10", python: "3.11.3" }. */
  tools: Record<string, string>;
  capabilities: string[];
  dataDir: string | null;
  freeDiskGb: number | null;
  /** The agent's review server on its own PC (loopback only) and the key it requires; `remote` is
   *  its Cloudflare Tunnel address, reached only through the Worker (/api/media/review/*). */
  review: { url: string; key: string; remote?: string } | null;
  /** The wall computer's photo copy through its Cloudflare Tunnel, relayed by /api/photos/feed/*.
   *  The key stays in the Worker: the admin page only sees `remote`. */
  wall?: { remote: string; key?: string } | null;
}

export interface MediaAgent {
  id: string;
  name: string;
  createdAt: string;
  lastSeenAt: string | null;
  online: boolean;
  info: MediaAgentInfo | null;
}

export interface MediaSource {
  id: string;
  name: string;
  /** UNC path, e.g. \\bsgqnap\photos */
  rootPath: string;
  /** Folder names or relative paths (with * wildcards) never read. */
  exclusions: string[];
  createdAt: string;
  updatedAt: string;
}

export interface MediaSourceInput {
  name: string;
  rootPath: string;
  exclusions: string[];
}

/** Live counters while a run is going. All optional: stages fill what they know. */
export interface MediaProgress {
  stage: string;
  stageLabel: string;
  done: number;
  total: number | null;
  bytesDone?: number;
  bytesTotal?: number | null;
  filesPerSec?: number | null;
  mbPerSec?: number | null;
  etaSeconds?: number | null;
  failed?: number;
  message?: string | null;
}

export interface MediaRun {
  id: string;
  sourceId: string;
  kind: MediaRunKind;
  status: MediaRunStatus;
  control: MediaRunControl;
  createdAt: string;
  createdBy: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string;
  agentId: string | null;
  /** True while the run is unfinished but no agent holds it (PC off or asleep). */
  waitingForAgent: boolean;
  progress: MediaProgress | null;
  error: string | null;
  hasReport: boolean;
}

export interface MediaRunDetail extends MediaRun {
  report: MediaReport | null;
}

export interface MediaOverview {
  agents: MediaAgent[];
  sources: MediaSource[];
  runs: MediaRun[];
}

// ---------- Reports ----------

export interface CountBytes {
  files: number;
  bytes: number;
}

export interface FolderSummary extends CountBytes {
  folder: string;
  photos: number;
  videos: number;
  withDate: number;
  missingDate: number;
  dateConflicts: number;
  earliest: string | null;
  latest: string | null;
}

export interface InventoryReport {
  kind: "inventory";
  generatedAt: string;
  rootPath: string;
  durations: Record<string, number>;
  totals: CountBytes & { photos: number; videos: number; sidecars: number; other: number; excludedFolders: number };
  byFormat: (CountBytes & { ext: string; kind: "photo" | "video" | "sidecar" | "other" })[];
  byFolder: FolderSummary[];
  years: { year: number; files: number }[];
  /** Where each photo's date came from (best source wins). */
  dateSources: Record<string, number>;
  dateConflicts: number;
  withGps: number;
  cameras: { model: string; files: number }[];
  issues: { unreadable: number; zeroByte: number; metadataFailed: number };
  /** A few examples only; the full list stays on the PC. */
  examples: { reason: string; path: string }[];
}

export interface BenchmarkStage {
  stage: string;
  label: string;
  files: number;
  seconds: number;
  bytes?: number;
}

export interface BenchmarkProjection {
  stage: string;
  label: string;
  hours: number;
  basis: string;
}

export interface BenchmarkReport {
  kind: "benchmark";
  generatedAt: string;
  rootPath: string;
  sample: CountBytes & { photos: number; videos: number; formats: Record<string, number> };
  library: CountBytes & { photos: number; videos: number };
  stages: BenchmarkStage[];
  network: { readMbPerSec: number; pcLink: string | null; note: string };
  projections: BenchmarkProjection[];
  assumptions: string[];
}

export interface AnalysisReport {
  kind: "analysis";
  scope: "sample" | "all";
  generatedAt: string;
  rootPath: string;
  counts: { files: number; images: number; faces: number; people: number; captions: number; flagged: number; groups: number; inGroups: number; extraCopies: number };
  tags: { tag: string; files: number }[];
  flags: { category: string; files: number }[];
  /** Kept private: automatically (flagged or very like a private photo), by a parent; "fine" = a parent let it out.
   *  The wall takes only photos in approved folders that aren't private. Missing on reports from before. */
  privacy?: { auto: number; you: number; fine: number; folders: number; foldersApproved: number; wallEligible: number; skippedVideos?: number };
  stages: BenchmarkStage[];
  projections: BenchmarkProjection[];
  models: Record<string, string>;
  /** "Process new photos" runs: what the scan found and how many files were read and analysed. */
  update?: { newFiles: number; changedFiles: number; missingFiles: number; read: number };
}

export type MediaReport = InventoryReport | BenchmarkReport | AnalysisReport;

// ---------- Validation ----------

export class MediaInputError extends Error {}

const UNC = /^\\\\[^\\/:*?"<>|]+\\[^\\/:*?"<>|]+(\\[^\\/:*?"<>|]+)*\\?$/;

/** Only home-network shares (\\server\share\…) are accepted as roots. */
export function normalizeRootPath(raw: unknown): string {
  if (typeof raw !== "string") throw new MediaInputError("Enter the share path, like \\\\nas\\photos");
  const p = raw.trim().replace(/\//g, "\\").replace(/\\+$/, "");
  if (!UNC.test(p) || p.length > 260) throw new MediaInputError("Use a network share path, like \\\\nas\\photos");
  return p;
}

export function normalizeExclusions(raw: unknown): string[] {
  const list = Array.isArray(raw) ? raw : typeof raw === "string" ? raw.split(/\r?\n/) : [];
  const out: string[] = [];
  for (const item of list) {
    if (typeof item !== "string") continue;
    const s = item.trim().replace(/\//g, "\\").replace(/^\\+|\\+$/g, "");
    if (!s) continue;
    if (s.length > 200 || s.includes("..")) throw new MediaInputError(`Exclusion "${s.slice(0, 40)}" isn't a folder path inside the share`);
    if (!out.includes(s)) out.push(s);
  }
  if (out.length > 100) throw new MediaInputError("At most 100 exclusions");
  return out;
}

export function parseSourceInput(body: Record<string, unknown>): MediaSourceInput {
  const rootPath = normalizeRootPath(body.rootPath);
  const name = typeof body.name === "string" && body.name.trim() ? body.name.trim().slice(0, 60) : rootPath.split("\\").filter(Boolean).slice(-1)[0]!;
  return { name, rootPath, exclusions: normalizeExclusions(body.exclusions) };
}

export function isRunKind(v: unknown): v is MediaRunKind {
  return typeof v === "string" && (MEDIA_RUN_KINDS as readonly string[]).includes(v);
}

export function isFinished(status: MediaRunStatus): boolean {
  return FINISHED_STATUSES.includes(status);
}

/** "8.Wedding" → "Wedding": QNAP folders are often numbered for sort order. */
export function folderLabel(folder: string): string {
  return folder.replace(/^\d+[.\s_-]+/, "") || folder;
}
